import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, ShieldOff, ShieldCheck, RefreshCw, UserPlus } from "lucide-react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  adminAddTesters,
  adminAdjustTesterCredits,
  adminListTesters,
  adminSetTesterRevoked,
  type TesterRow,
} from "@/lib/visa-readiness/testers.functions";

export const Route = createFileRoute("/admin/visa-testers")({
  head: () => ({
    meta: [
      { title: "Visa Readiness Testers - Admin" },
      { name: "description", content: "Manage private-preview access to the Visa Readiness Assessment." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: TesterAdminPage,
});

function TesterAdminPage() {
  const listFn = useServerFn(adminListTesters);
  const addFn = useServerFn(adminAddTesters);
  const adjustFn = useServerFn(adminAdjustTesterCredits);
  const revokeFn = useServerFn(adminSetTesterRevoked);

  const [rows, setRows] = useState<TesterRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [emails, setEmails] = useState("");
  const [credits, setCredits] = useState(15);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listFn();
      setRows(data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load testers");
    } finally {
      setLoading(false);
    }
  }, [listFn]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleAdd = async () => {
    if (!emails.trim()) return toast.error("Enter at least one email");
    setSubmitting(true);
    try {
      const { results } = await addFn({ data: { emails, credits, notes: notes || undefined } });
      const added = results.filter((r) => r.status === "added").length;
      const updated = results.filter((r) => r.status === "updated").length;
      const missing = results.filter((r) => r.status === "not_found").map((r) => r.email);
      toast.success(`Added ${added}, updated ${updated}${missing.length ? `; not signed up: ${missing.join(", ")}` : ""}`);
      setEmails("");
      setNotes("");
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to add tester");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdjust = async (user_id: string, delta: number) => {
    try {
      await adjustFn({ data: { user_id, delta } });
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to adjust credits");
    }
  };

  const handleRevoke = async (user_id: string, revoked: boolean) => {
    try {
      await revokeFn({ data: { user_id, revoked } });
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update access");
    }
  };

  return (
    <AdminShell
      title="Visa Readiness Testers"
      description="Whitelist users for the private-preview Visa Readiness Assessment and manage their scan quotas."
      actions={
        <Button variant="outline" size="sm" onClick={refresh}>
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      }
    >
      <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
        {/* Add tester panel */}
        <section className="rounded-2xl border bg-card p-5">
          <div className="flex items-center gap-2">
            <UserPlus className="h-4 w-4 text-primary" />
            <h2 className="font-semibold">Add tester(s)</h2>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Emails must already have a signed-up account. Comma or newline separated.
          </p>
          <div className="mt-4 space-y-3">
            <div>
              <Label htmlFor="emails">Email(s)</Label>
              <Textarea
                id="emails"
                rows={3}
                placeholder="tester@example.com, another@x.com"
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="credits">Scan quota</Label>
              <Input
                id="credits"
                type="number"
                min={1}
                max={9999}
                value={credits}
                onChange={(e) => setCredits(Math.max(1, Number(e.target.value) || 0))}
              />
              <div className="mt-1 flex flex-wrap gap-1.5">
                {[5, 10, 15, 25, 100].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setCredits(n)}
                    className="rounded-full border px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-accent"
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="notes">Notes (optional)</Label>
              <Input
                id="notes"
                placeholder="e.g. Cohort 1 beta"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={500}
              />
            </div>
            <Button className="w-full" onClick={handleAdd} disabled={submitting}>
              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
              Grant access
            </Button>
          </div>
        </section>

        {/* Table */}
        <section className="rounded-2xl border bg-card">
          <div className="flex items-center justify-between border-b px-5 py-3">
            <h2 className="font-semibold">Active testers</h2>
            <span className="text-xs text-muted-foreground">{rows.length} total</span>
          </div>
          {loading ? (
            <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading testers…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">No testers yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">Email</th>
                    <th className="px-4 py-2">Added</th>
                    <th className="px-4 py-2">Scans left / quota</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const quota = r.initial_credits || r.credits_remaining;
                    return (
                      <tr key={r.user_id} className="border-t">
                        <td className="px-4 py-2 font-medium">
                          {r.email ?? <span className="text-muted-foreground">-</span>}
                          {r.notes && <div className="text-xs text-muted-foreground">{r.notes}</div>}
                        </td>
                        <td className="px-4 py-2 text-muted-foreground">
                          {new Date(r.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-2">
                          <span className="font-semibold">{r.credits_remaining}</span>
                          <span className="text-muted-foreground"> / {quota}</span>
                        </td>
                        <td className="px-4 py-2">
                          {r.revoked ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">
                              Revoked
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700">
                              Active
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="outline" onClick={() => handleAdjust(r.user_id, 5)}>
                              +5
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleAdjust(r.user_id, 15)}>
                              +15
                            </Button>
                            {r.revoked ? (
                              <Button size="sm" variant="secondary" onClick={() => handleRevoke(r.user_id, false)}>
                                <ShieldCheck className="mr-1 h-3.5 w-3.5" /> Reactivate
                              </Button>
                            ) : (
                              <Button size="sm" variant="destructive" onClick={() => handleRevoke(r.user_id, true)}>
                                <ShieldOff className="mr-1 h-3.5 w-3.5" /> Revoke
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </AdminShell>
  );
}
